// client/src/services/reportService.ts

import { apiClient } from './api';

// Types pour les rapports de performance
export interface DriverPerformanceMetrics {
  driver_id: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
  email: string;
  license_number: string;
  license_expiry: string;
  hire_date: string;
  is_active: boolean;
  total_missions: number;
  completed_missions: number;
  ongoing_missions: number;
  cancelled_missions: number;
  completion_rate: number;
  first_mission_date: string;
  last_mission_date: string;
  avg_fuel_consumption: number;
  total_fuel_cost: number;
  total_refuels: number;
  total_distance_km: number;
  missions_this_month: number;
  missions_this_year: number;
  avg_rating: number;
  last_activity_date: string;
}

export interface VehiclePerformanceMetrics {
  vehicle_id: string;
  tenant_id: string;
  license_plate: string;
  brand: string;
  model: string;
  vehicle_type: string;
  year: number;
  status: string;
  purchase_date: string;
  total_missions: number;
  completed_missions: number;
  ongoing_missions: number;
  total_maintenances: number;
  total_maintenance_cost: number;
  last_maintenance_date: string;
  preventive_maintenances: number;
  corrective_maintenances: number;
  total_refuels: number;
  total_fuel_cost: number;
  total_fuel_quantity: number;
  avg_fuel_consumption: number;
  best_fuel_consumption: number;
  worst_fuel_consumption: number;
  last_refuel_date: string;
  current_mileage: number;
  total_distance_km: number;
  total_operating_cost: number;
  cost_per_km: number;
  missions_this_month: number;
  last_activity_date: string;
}

export interface FleetKPIs {
  tenant_id: string;
  tenant_name: string;
  total_vehicles: number;
  active_vehicles: number;
  vehicles_in_maintenance: number;
  total_drivers: number;
  active_drivers: number;
  total_missions: number;
  completed_missions: number;
  ongoing_missions: number;
  pending_missions: number;
  mission_completion_rate: number;
  missions_this_month: number;
  completed_missions_this_month: number;
  total_fuel_cost: number;
  total_maintenance_cost: number;
  total_operating_cost: number;
  avg_fleet_consumption: number;
  total_distance_km: number;
  fuel_cost_this_month: number;
  maintenance_cost_this_month: number;
  vehicle_utilization_rate: number;
}

export interface PerformanceReport {
  period: {
    start: string;
    end: string;
  };
  fleet_kpis: FleetKPIs;
  fleet_overview: {
    overview: any;
    top_performers: any;
    alerts: any;
  };
  mission_statistics: MissionStatistics;
  top_performers: {
    drivers: TopDriver[];
    vehicles: TopVehicle[];
  };
  summary: {
    total_drivers: number;
    active_drivers: number;
    total_vehicles: number;
    active_vehicles: number;
  };
}

export interface MissionStatistics {
  total_missions: number;
  completed: number;
  in_progress: number;
  pending: number;
  cancelled: number;
  completion_rate: number;
  vehicles_used: number;
  drivers_active: number;
  avg_duration_hours: number;
}

export interface TopDriver {
  driver_id: string;
  first_name: string;
  last_name: string;
  total_missions: number;
  completed_missions: number;
  completion_rate: number;
  avg_fuel_consumption: number;
  total_distance_km: number;
}

export interface TopVehicle {
  vehicle_id: string;
  license_plate: string;
  brand: string;
  model: string;
  total_missions: number;
  completed_missions: number;
  avg_fuel_consumption: number;
  total_operating_cost: number;
  cost_per_km: number;
}

export interface DriverPerformanceReport {
  period: {
    start: string;
    end: string;
  };
  driver_info: {
    driver_id: string;
    first_name: string;
    last_name: string;
    email: string;
    license_number: string;
    hire_date: string;
    is_active: boolean;
  };
  mission_stats: {
    total_missions: number;
    completed_missions: number;
    completion_rate: number;
    cancelled_missions: number;
  };
  fuel_stats: {
    total_refuels: number;
    total_fuel_cost: number;
    avg_consumption: number;
    total_distance: number;
  };
  performance_trend: Array<{
    month: string;
    missions_completed: number;
    fuel_cost: number;
    avg_consumption: number;
  }>;
}

export interface VehiclePerformanceReport {
  period: {
    start: string;
    end: string;
  };
  vehicle_info: {
    vehicle_id: string;
    license_plate: string;
    brand: string;
    model: string;
    year: number;
    vehicle_type: string;
    status: string;
  };
  mission_stats: {
    total_missions: number;
    completed_missions: number;
    ongoing_missions: number;
  };
  maintenance_stats: {
    total_maintenances: number;
    total_maintenance_cost: number;
    preventive_count: number;
    corrective_count: number;
    last_maintenance: string;
  };
  fuel_stats: {
    total_refuels: number;
    total_fuel_cost: number;
    total_quantity: number;
    avg_consumption: number;
    total_distance: number;
  };
  cost_breakdown: {
    fuel_cost: number;
    maintenance_cost: number;
    total_cost: number;
    cost_per_km: number;
  };
}

export interface MissionReport {
  period: {
    start: string;
    end: string;
  };
  statistics: MissionStatistics;
  monthly_trend: Array<{
    month: string;
    total_missions: number;
    completed_missions: number;
    cancelled_missions: number;
    completion_rate: number;
  }>;
  by_mission_type: Array<{
    mission_type: string;
    total_missions: number;
    completed: number;
    completion_rate: number;
    avg_duration_hours: number;
  }>;
}

export interface PerformanceLeaderboard {
  top_drivers: TopDriver[];
  top_vehicles: TopVehicle[];
}

/**
 * Service pour les rapports et analytics
 */
export class ReportService {
  private readonly baseUrl = '/admin/reports';

  /**
   * Récupérer le rapport de performance global
   */
  async getPerformanceReport(startDate?: string, endDate?: string): Promise<PerformanceReport> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const url = apiClient.buildUrl(`${this.baseUrl}/performance`, params);
    return apiClient.get<PerformanceReport>(url);
  }

  /**
   * Récupérer les KPIs de la flotte
   */
  async getFleetKPIs(): Promise<FleetKPIs> {
    return apiClient.get<FleetKPIs>(`${this.baseUrl}/kpis`);
  }

  /**
   * Récupérer la vue d'ensemble de la flotte
   */
  async getFleetOverview(): Promise<{
    overview: any;
    top_performers: any;
    alerts: any;
  }> {
    return apiClient.get<{
      overview: any;
      top_performers: any;
      alerts: any;
    }>(`${this.baseUrl}/fleet-overview`);
  }

  /**
   * Récupérer les statistiques de missions
   */
  async getMissionStatistics(startDate?: string, endDate?: string): Promise<MissionReport> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const url = apiClient.buildUrl(`${this.baseUrl}/missions`, params);
    return apiClient.get<MissionReport>(url);
  }

  /**
   * Récupérer les classements de performance
   */
  async getPerformanceLeaderboard(limit: number = 10): Promise<PerformanceLeaderboard> {
    const url = apiClient.buildUrl(`${this.baseUrl}/leaderboard`, { limit: limit.toString() });
    return apiClient.get<PerformanceLeaderboard>(url);
  }

  /**
   * Récupérer le rapport de performance d'un conducteur
   */
  async getDriverPerformance(
    driverId: string,
    startDate?: string,
    endDate?: string
  ): Promise<DriverPerformanceReport> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const url = apiClient.buildUrl(`${this.baseUrl}/drivers/${driverId}/performance`, params);
    return apiClient.get<DriverPerformanceReport>(url);
  }

  /**
   * Récupérer le rapport de performance d'un véhicule
   */
  async getVehiclePerformance(
    vehicleId: string,
    startDate?: string,
    endDate?: string
  ): Promise<VehiclePerformanceReport> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const url = apiClient.buildUrl(`${this.baseUrl}/vehicles/${vehicleId}/performance`, params);
    return apiClient.get<VehiclePerformanceReport>(url);
  }
}

// Instance singleton du service
export const reportService = new ReportService();
